import { useEffect, useState } from "react";
import { Loader2, Copy, CheckCircle2, MailCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

/** Invitation d'un partenaire : société, type, coordonnées, modules (pré-remplis depuis le Builder de la société). */
export function PartnerInviteDialog({ api, societes, registry, onClose, onInvited }) {
  const [form, setForm] = useState({ societe_id: societes[0]?.societe_id || "", type: "fournisseur", name: "", company: "", email: "", phone: "" });
  const [access, setAccess] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const modules = registry?.partner_modules?.[form.type] || [];

  useEffect(() => {
    if (!form.societe_id) return;
    api.get(`/societes/${form.societe_id}/builder`).then((r) => {
      const d = r.data.config?.partner_access?.[form.type] || {};
      setDefaults(d);
      setAccess(d);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [form.societe_id, form.type]);

  const submit = async () => {
    if (!form.email.includes("@")) { toast.error("Email invalide"); return; }
    if (!form.societe_id) { toast.error("Choisissez une société"); return; }
    setLoading(true);
    try {
      const overrides = Object.fromEntries(Object.entries(access).filter(([k, v]) => v !== defaults[k]));
      const r = await api.post("/partners", { ...form, access_overrides: overrides });
      setResult(r.data);
      onInvited(r.data);
      toast.success(r.data.email_sent ? "Invitation envoyée par email" : "Partenaire créé — copiez le lien d'invitation");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  const copy = async () => { await navigator.clipboard.writeText(result.invite_url); toast.success("Lien copié"); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-invite-dialog">
        <DialogHeader><DialogTitle>{result ? "Invitation créée" : "Inviter un partenaire"}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="partner-invite-result">
            <div className={"flex items-center gap-2 text-sm rounded-xl p-3 " + (result.email_sent ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300")}>
              {result.email_sent ? <MailCheck className="w-4 h-4" /> : <MailWarning className="w-4 h-4" />}
              {result.email_sent ? `Email envoyé à ${result.email}` : "Email non envoyé (SMTP non configuré) — transmettez le lien ci-dessous au partenaire."}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Lien d'activation</div>
            <div className="flex gap-2">
              <Input readOnly value={result.invite_url} className="bg-[#14101c] border-white/10 text-white font-mono text-xs" data-testid="partner-invite-url" />
              <Button onClick={copy} variant="outline" className="border-white/10 text-slate-200" data-testid="partner-invite-copy"><Copy className="w-4 h-4" /></Button>
            </div>
            <div className="flex justify-end"><Button onClick={onClose} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="partner-invite-done"><CheckCircle2 className="w-4 h-4 mr-1" /> Terminer</Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <L label="Société">
                <select value={form.societe_id} onChange={(e) => setForm({ ...form, societe_id: e.target.value })} className="w-full bg-[#14101c] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="partner-invite-societe">
                  {societes.map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
                </select>
              </L>
              <L label="Type de partenaire">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-[#14101c] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="partner-invite-type">
                  {(registry?.partner_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </L>
              <L label="Nom du contact"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-invite-name" /></L>
              <L label="Entreprise"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-invite-company" /></L>
              <L label="Email (identifiant)"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-invite-email" /></L>
              <L label="Téléphone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#14101c] border-white/10 text-white" data-testid="partner-invite-phone" /></L>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Modules accessibles <span className="text-slate-600 normal-case tracking-normal">(défauts du Builder de la société, ajustables)</span></div>
              <div className="space-y-1.5">
                {modules.map((m) => (
                  <label key={m.key} className="flex items-center justify-between gap-3 bg-[#14101c] border border-white/5 rounded-lg px-3 py-2 text-xs cursor-pointer">
                    <div><span className="text-slate-100 font-semibold">{m.label}</span><span className="text-slate-500 ml-2">{m.desc}</span></div>
                    <Switch checked={!!access[m.key]} onCheckedChange={(v) => setAccess((a) => ({ ...a, [m.key]: v }))} data-testid={"partner-invite-module-" + m.key} />
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} className="text-slate-400">Annuler</Button>
              <Button onClick={submit} disabled={loading} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="partner-invite-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Envoyer l'invitation</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function L({ label, children }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>{children}</div>;
}
